import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  updateURLParams,
  urlParamsToObject,
} from "../../../../../../util/urlManager";
import {
  EMPLOYEE_AVAILABILITY,
  EMPLOYEE_LAST_ACTIVITY,
  EMPLOYEE_STATUS,
  TEmployeeLastActivity,
  TEmployeeScore,
  TEmployeeTimeSlot,
} from "../../../../../../api/employee/employee.types";
import { ECandidateProfileTab } from "../../types/candidate.types";
import { parseCandidateProfileTab } from "../../util/candidate.util";

// Este filtro siempre debe viajar en la url, incluso con su valor por defecto,
// para que la búsqueda quede explícita al compartir o recargar el enlace.
const ALWAYS_IN_URL_FILTER = "lastActivity";

export const initialState = {
  search: "",
  titulacion: "",
  sexo: "",
  estadoCuideo: [
    EMPLOYEE_STATUS.ACTIVE,
    EMPLOYEE_STATUS.VALIDATED,
    EMPLOYEE_STATUS.VALIDATED_PLUS,
    EMPLOYEE_STATUS.NOT_VALIDATED,
  ],
  felizVitaStatus: [],
  businessCountry: "es",
  ciudad: "",
  provincia: "",
  department: "",
  carnet: "",
  vehiculo: "",
  lugarDeNacimiento: "",
  selectedEmployeeId: "",
  edad: ["26-35", "36-45", "46-55", "56-60"] as string[],
  experience: [] as string[],
  tipo: [] as string[],
  inOpenProcess: "no",
  inRecentProcesses: "",
  orden: "recientes" as "recientes" | "antiguos" | "activos",
  vista: "lista",
  "latitud[gt]": undefined,
  "latitud[lt]": undefined,
  "longitud[gt]": undefined,
  "longitud[lt]": undefined,
  x: undefined,
  y: undefined,
  z: undefined,
  enMapa: "no",
  tasks: [] as string[],
  languages: [] as string[],
  pathologies: [] as string[],
  interviewStatus: [] as string[],
  company: "",
  workRegime: [] as string[],
  page: 1,
  eTab: ECandidateProfileTab.PROFILE,
  ready: false,
  hasWhatsapp: "yes",
  botAvailable: "",
  isSelected: "",
  availability: [
    EMPLOYEE_AVAILABILITY.AVAILABLE,
  ] as typeof EMPLOYEE_AVAILABILITY[keyof typeof EMPLOYEE_AVAILABILITY][],
  contractedFv: '0',
  hasTimeSlot: [] as TEmployeeTimeSlot[],
  hasEmployeeScore: [] as TEmployeeScore[],
  preferenceSchedule: "",
  lastActivity: EMPLOYEE_LAST_ACTIVITY.LESS_12_MONTHS as TEmployeeLastActivity,
};

export const resetState = {
  titulacion: "",
  sexo: "",
  estadoCuideo: [
    EMPLOYEE_STATUS.ACTIVE,
    EMPLOYEE_STATUS.VALIDATED,
    EMPLOYEE_STATUS.VALIDATED_PLUS,
    EMPLOYEE_STATUS.NOT_VALIDATED,
  ],
  felizVitaStatus: [],
  ciudad: "",
  provincia: "",
  department: "",
  carnet: "",
  vehiculo: "",
  lugarDeNacimiento: "",
  edad: ["26-35", "36-45", "46-55", "56-60"] as string[],
  experience: [] as string[],
  tipo: [] as string[],
  inOpenProcess: "no",
  inRecentProcesses: "",
  tasks: [] as string[],
  languages: [] as string[],
  pathologies: [] as string[],
  interviewStatus: [] as string[],
  company: "",
  workRegime: [] as string[],
  hasWhatsapp: "yes",
  botAvailable: "",
  isSelected: "",
  availability: [
    EMPLOYEE_AVAILABILITY.AVAILABLE,
  ] as typeof EMPLOYEE_AVAILABILITY[keyof typeof EMPLOYEE_AVAILABILITY][],
  substitutions: "no",
  contractedFv: '0',
  hasTimeSlot: "",
  hasEmployeeScore: "",
  preferenceSchedule: "",
  lastActivity: EMPLOYEE_LAST_ACTIVITY.LESS_12_MONTHS as TEmployeeLastActivity,
};

export type TEmployeesPageFilters = typeof initialState;

// Valores sin filtrar, incluidos los que tienen valor por defecto, para el
// botón de limpiar filtros
const clearedState = {
  titulacion: "",
  sexo: "",
  estadoCuideo: [] as typeof initialState.estadoCuideo,
  felizVitaStatus: [],
  ciudad: "",
  provincia: "",
  department: "",
  carnet: "",
  vehiculo: "",
  lugarDeNacimiento: "",
  edad: [] as string[],
  experience: [] as string[],
  tipo: [] as string[],
  inOpenProcess: "",
  inRecentProcesses: "",
  tasks: [] as string[],
  languages: [] as string[],
  pathologies: [] as string[],
  interviewStatus: [] as string[],
  company: "",
  workRegime: [] as string[],
  hasWhatsapp: "",
  botAvailable: "",
  isSelected: "",
  availability: [] as typeof EMPLOYEE_AVAILABILITY[keyof typeof EMPLOYEE_AVAILABILITY][],
  substitutions: "",
  contractedFv: "",
  hasTimeSlot: [] as TEmployeeTimeSlot[],
  hasEmployeeScore: [] as TEmployeeScore[],
  preferenceSchedule: "",
  lastActivity: "" as TEmployeeLastActivity,
};

// Deja sin valor todos los filtros que se ven en la barra de filtros (incluido
// el buscador y los que tienen valor por defecto). El mapa (enMapa y su
// rectángulo) se conserva para seguir buscando en la zona en la que está la
// asesora. Los parámetros forzados en la llamada a la API se siguen añadiendo
// en parseQueryParams.
export const getClearedEmployeesPageFilters = (
  filters: TEmployeesPageFilters
): TEmployeesPageFilters => {
  return {
    ...filters,
    ...clearedState,
    search: "",
    page: 1,
  };
};

export default function useEmployeesPageURLParams(
  searchString: string,
  history: any,
  location: any
) {
  const [filters, setFiltersState] = useState({ ...initialState });

  useEffect(() => {
    if (history.action !== "REPLACE") {
      const params = history.location.search
        ? urlParamsToObject(history.location.search)
        : {};

      if (params.page && typeof params.page !== "number") {
        params.page = parseInt(params.page);
      }
      if (params.eTab !== undefined) {
        params.eTab = parseCandidateProfileTab(params.eTab);
      }
      Object.keys(params).forEach((key) => {
        if (
          Array.isArray((initialState as any)[key]) &&
          typeof params[key] === "string"
        ) {
          params[key] = [params[key]];
        }
        if (
          typeof (initialState as any)[key] === "string" &&
          Array.isArray(params[key])
        ) {
          params[key] = params[key].join(",");
        }
      });

      if (!params[ALWAYS_IN_URL_FILTER]) {
        params[ALWAYS_IN_URL_FILTER] = initialState[ALWAYS_IN_URL_FILTER];
        updateURLParams(params);
      }

      setFiltersState((prevFilters) => {
        if (!_.some([prevFilters], params)) {
          return {
            ...prevFilters,
            ...params,
            ready: true,
          };
        } else {
          return {
            ...prevFilters,
            ready: true,
          };
        }
      });
    }
  }, [history, setFiltersState]);

  const setFilters = useCallback(
    (newFilters: TEmployeesPageFilters) => {
      const params = {
        ...newFilters,
        "latitud[gt]":
          newFilters.enMapa === "no" ? undefined : newFilters["latitud[gt]"],
        "latitud[lt]":
          newFilters.enMapa === "no" ? undefined : newFilters["latitud[lt]"],
        "longitud[gt]":
          newFilters.enMapa === "no" ? undefined : newFilters["longitud[gt]"],
        "longitud[lt]":
          newFilters.enMapa === "no" ? undefined : newFilters["longitud[lt]"],
        x: newFilters.enMapa === "no" ? undefined : newFilters.x,
        y: newFilters.enMapa === "no" ? undefined : newFilters.y,
        z: newFilters.enMapa === "no" ? undefined : newFilters.z,
      } as any;
      setFiltersState(newFilters);

      // Parse a bit wich parameters to set on url
      delete params.ready;
      delete params["longitud[gt]"];
      delete params["longitud[lt]"];
      delete params["latitud[gt]"];
      delete params["latitud[lt]"];

      // Clean not needed parameters
      Object.keys(initialState).forEach((key) => {
        if (key === ALWAYS_IN_URL_FILTER) {
          return;
        }
        if (Array.isArray((initialState as any)[key])) {
          // Un filtro vacío no se guarda en la url
          if (
            _.isEmpty(params[key]) ||
            _.isEqual(
              _.sortBy((initialState as any)[key]),
              _.sortBy(params[key])
            )
          ) {
            delete params[key];
          }
        } else {
          if (params[key] === (initialState as any)[key]) {
            delete params[key];
          }
        }
      });

      updateURLParams(params);
    },
    [setFiltersState]
  );

  return { filters, setFilters };
}
